import { apiClient } from '@/shared/api/apiClient'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Partnership,
  PartnershipCreationPayload,
  PartnershipInvitationPayload
} from '@/common'

const queryKey = 'partnerships'

export const useListPartnerships = (organizationId: string) => {
  return useQuery({
    queryKey: [queryKey],
    queryFn: async () => {
      const response = await apiClient.get<Partnership[]>(
        `/v1/organizations/${organizationId}/partnerships`
      )

      return response.data
    }
  })
}

export const useCreatePartnership = (
  organizationId: string,
  options?: Omit<Parameters<typeof useMutation>[0], 'mutationFn'>
) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: PartnershipCreationPayload) => {
      const response = await apiClient.post<Partnership>(
        `/v1/organizations/${organizationId}/partnerships`,
        data
      )
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      return response.data
    },
    ...options
  })
}

export const useInvitePartnership = (organizationId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      partnershipId,
      payload
    }: {
      partnershipId: string
      payload: PartnershipInvitationPayload
    }) => {
      const response = await apiClient.post<Partnership>(
        `/v1/organizations/${organizationId}/partnerships/${partnershipId}/invite`,
        payload
      )
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      return response.data
    }
  })
}

export const useAcceptPartnershipInvitation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      partnershipId,
      organizationId
    }: {
      partnershipId: string
      organizationId: string
    }) => {
      const response = await apiClient.post<Partnership>(
        `/v1/organizations/${organizationId}/partnerships/${partnershipId}/accept`,
        {}
      )
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      return response.data
    }
  })
}

export const useRejectPartnershipInvitation = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      partnershipId,
      organizationId,
      reason
    }: {
      partnershipId: string
      organizationId: string
      reason?: string
    }) => {
      const response = await apiClient.post<Partnership>(
        `/v1/organizations/${organizationId}/partnerships/${partnershipId}/reject`,
        { reason }
      )
      queryClient.invalidateQueries({ queryKey: [queryKey] })
      return response.data
    }
  })
}
